import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAccessibility } from "@/context/AccessibilityContext";
import { Accessibility, X, RotateCcw, Type, Contrast, Sparkles, Underline, BookOpen } from "lucide-react";

const FONT_STEPS = [
  { value: 0.9,  key: "a11y.font_smaller" },
  { value: 1,    key: "a11y.font_default" },
  { value: 1.15, key: "a11y.font_larger" },
  { value: 1.35, key: "a11y.font_largest" },
];

export default function AccessibilityToolbar() {
  const { t } = useTranslation();
  const { prefs, setFontScale, toggle, reset } = useAccessibility();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  // Escape closes
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <>
      <button
        data-testid="a11y-fab"
        aria-label={open ? t("a11y.close_menu") : t("a11y.open_menu")}
        aria-expanded={open}
        aria-controls="a11y-panel"
        onClick={() => setOpen((o) => !o)}
        className="fixed z-[95] bottom-6 left-6 w-12 h-12 rounded-full bg-brand hover:bg-brand-dark text-ink flex items-center justify-center shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
      >
        <Accessibility className="w-6 h-6" aria-hidden="true" />
      </button>

      {open && (
        <div
          id="a11y-panel"
          ref={panelRef}
          role="dialog"
          aria-label={t("a11y.title")}
          className="fixed z-[96] bottom-24 left-6 w-80 bg-surface border border-ink/15 shadow-2xl p-5 text-ink"
          data-testid="a11y-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 font-display font-bold">
              <Accessibility className="w-5 h-5 text-teal" aria-hidden="true" /> {t("a11y.title")}
            </div>
            <button
              data-testid="a11y-close"
              aria-label={t("a11y.close_menu")}
              onClick={() => setOpen(false)}
              className="text-ink/60 hover:text-ink"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          {/* Font size */}
          <div className="mb-5">
            <div className="flex items-center gap-2 tag mb-2">
              <Type className="w-3.5 h-3.5" aria-hidden="true" /> {t("a11y.font_size")}
            </div>
            <div role="radiogroup" aria-label={t("a11y.font_size")} className="grid grid-cols-4 gap-1">
              {FONT_STEPS.map((s) => (
                <button
                  key={s.value}
                  role="radio"
                  aria-checked={prefs.fontScale === s.value}
                  data-testid={`a11y-font-${s.value}`}
                  onClick={() => setFontScale(s.value)}
                  className={`py-2 text-xs border ${
                    prefs.fontScale === s.value
                      ? "bg-brand border-teal text-ink"
                      : "bg-transparent border-ink/15 text-ink/70 hover:border-ink/40"
                  }`}
                >
                  <span
                    style={{ fontSize: `${0.7 + (s.value - 0.9) * 0.6}rem` }}
                    aria-hidden="true"
                  >A</span>
                  <span className="sr-only">{t(s.key)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <ToggleRow
              icon={Contrast}
              label={t("a11y.contrast")}
              on={prefs.highContrast}
              testId="a11y-toggle-contrast"
              onToggle={() => toggle("highContrast")}
            />
            <ToggleRow
              icon={Sparkles}
              label={t("a11y.motion")}
              on={prefs.reduceMotion}
              testId="a11y-toggle-motion"
              onToggle={() => toggle("reduceMotion")}
            />
            <ToggleRow
              icon={Underline}
              label={t("a11y.underline")}
              on={prefs.underlineLinks}
              testId="a11y-toggle-underline"
              onToggle={() => toggle("underlineLinks")}
            />
            <ToggleRow
              icon={BookOpen}
              label={t("a11y.readable_font")}
              on={prefs.readableFont}
              testId="a11y-toggle-readable"
              onToggle={() => toggle("readableFont")}
            />
          </div>

          <button
            data-testid="a11y-reset"
            onClick={reset}
            className="mt-5 w-full flex items-center justify-center gap-2 border border-ink/15 hover:border-ink/40 py-2 text-sm text-ink/80"
          >
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> {t("a11y.reset")}
          </button>
        </div>
      )}
    </>
  );
}

function ToggleRow({ icon: Icon, label, on, onToggle, testId }) {
  return (
    <button
      data-testid={testId}
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 border border-ink/10 hover:border-ink/30 transition-colors"
    >
      <span className="flex items-center gap-2 text-sm text-ink/80">
        <Icon className="w-4 h-4" aria-hidden="true" /> {label}
      </span>
      <span
        className={`w-9 h-5 relative rounded-full transition-colors ${
          on ? "bg-brand" : "bg-ink/10"
        }`}
        aria-hidden="true"
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            on ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}
